import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import LinearProgress from '@mui/material/LinearProgress';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { useState } from 'react';
import { formatDate, formatDateTime } from '../../lib/format';
import { usePowersensorStatus, useStartFetch, type FetchRun } from './api';

const MAX_DAYS = 92;

/** A date as YYYY-MM-DD in the browser's time zone, `daysAgo` days back. */
function localDay(daysAgo = 0): string {
  const d = new Date();
  d.setDate(d.getDate() - daysAgo);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/**
 * Download Powersensor readings between two dates: for one driver (`driverId`) or, without
 * it, for every account shared with Infinity EV. The download runs on the server; this
 * shows its progress and result. Only one runs at a time.
 */
export function FetchPanel({ driverId }: { driverId?: string }) {
  const status = usePowersensorStatus();
  const start = useStartFetch();
  const [from, setFrom] = useState(localDay(7));
  const [to, setTo] = useState(localDay());
  const [error, setError] = useState('');

  const days = from && to ? Math.round((Date.parse(to) - Date.parse(from)) / 86_400_000) + 1 : 0;
  const problem = !from || !to ? 'Choose both dates' : days < 1 ? 'The end date is before the start date' : days > MAX_DAYS ? `Choose at most ${MAX_DAYS} days at a time` : to > localDay() ? 'The end date is in the future' : '';
  const s = status.data;
  const run = s?.run ?? null;
  // Show the latest run here only if it's the same kind (this driver, or all accounts).
  const mine = run && (driverId ? run.kind === 'driver' : run.kind === 'all') ? run : null;
  const busy = !!s?.running || start.isPending;

  const fetchNow = async () => {
    setError('');
    try {
      await start.mutateAsync({ from, to, driverId });
    } catch (e) {
      setError((e as Error).message);
    }
  };

  if (s && !s.configured) {
    return <Alert severity="info">Powersensor isn't set up on this server, so readings can't be downloaded.</Alert>;
  }

  return (
    <Box sx={{ display: 'grid', gap: 2 }}>
      <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr 1fr', sm: '1fr 1fr auto' }, alignItems: 'start' }}>
        <TextField size="small" type="date" label="From" value={from} onChange={(e) => setFrom(e.target.value)} slotProps={{ inputLabel: { shrink: true }, htmlInput: { max: to || localDay() } }} />
        <TextField size="small" type="date" label="To" value={to} onChange={(e) => setTo(e.target.value)} slotProps={{ inputLabel: { shrink: true }, htmlInput: { min: from, max: localDay() } }} />
        <Button variant="contained" onClick={() => void fetchNow()} disabled={busy || !!problem} sx={{ gridColumn: { xs: '1 / -1', sm: 'auto' }, height: 40 }}>
          {driverId ? 'Download readings' : 'Download for all accounts'}
        </Button>
      </Box>
      <Typography variant="body2" color={problem && from && to ? 'error' : 'textSecondary'}>
        {problem || `${days} ${days === 1 ? 'day' : 'days'}, whole days in Sydney time. Readings already saved are updated, not doubled.`}
      </Typography>
      {error && <Alert severity="error">{error}</Alert>}
      {s?.running && run && !mine && <Alert severity="info">Another download is running ({run.label}). You can start this one when it finishes.</Alert>}
      {mine && <RunStatus run={mine} />}
    </Box>
  );
}

function RunStatus({ run }: { run: FetchRun }) {
  const pct = run.total ? Math.round((run.done / run.total) * 100) : 0;
  const range = `${formatDate(`${run.from}T00:00`)} to ${formatDate(`${run.to}T00:00`)}`;
  if (!run.finishedAt) {
    return (
      <Box aria-live="polite">
        <Typography variant="body2" sx={{ mb: 1 }}>
          Downloading {run.label}, {range}… {run.readings.toLocaleString()} readings so far
          {run.accounts > 1 ? `, ${run.accounts} accounts` : ''}.
        </Typography>
        <LinearProgress variant={run.total ? 'determinate' : 'indeterminate'} value={pct} aria-label="Download progress" />
      </Box>
    );
  }
  const finished = `Finished ${formatDateTime(run.finishedAt)}: ${run.label}, ${range}.`;
  if (run.error) return <Alert severity="error">{finished} The download failed: {run.error}</Alert>;
  return (
    <Alert severity={run.failed.length ? 'warning' : 'success'} aria-live="polite">
      {finished} {run.readings.toLocaleString()} readings{run.accounts > 1 ? ` from ${run.accounts} accounts` : ''}.
      {run.failed.length > 0 && ` These accounts couldn't be downloaded: ${run.failed.join(', ')}.`}
    </Alert>
  );
}
